"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, FileText, X } from "lucide-react";
import { EditorContent, useEditor } from "@tiptap/react";
import type { Editor } from "@tiptap/core";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import { HocuspocusProvider } from "@hocuspocus/provider";
import { createLowlight, common } from "lowlight";
import * as Y from "yjs";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { MarkdownViewerSaveStatus, type CollaborativeSaveState } from "@/components/markdown-viewer-save-status";
import { createMarkdownEditorExtensions } from "@/components/markdown-editor-extensions";
import { extractFileToken } from "@/lib/file-links";
import { extractFilePathFromToken } from "@/lib/file-link-rewrite";
import type { AccessTokenGetter } from "@/lib/utils";
import type { MarkdownViewerPanelProps } from "@/components/markdown-viewer-panel";
import { durableVectorCovers, isUnsupportedCapability } from "@/lib/collab-state";
import { useIsMobile } from "@/hooks/use-media-query";
import { restoreWikiLinks } from "@/lib/wiki-links";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { commentAnchorExtension, createCommentAnchor, resolveCommentRange, type CommentThread } from "@/components/collab-comment-anchors";

type Room = {
  docId: string; tenantId: string; roomToken: string; expiresAt: number;
  sourceETag: string; yStateVersion: number; projectedVersion: number;
  lastProjectionStatus: string; stateVector: string;
  originAgentId?: string | null; originSessionId?: string | null;
};
type Status = Pick<Room, "docId" | "sourceETag" | "yStateVersion" | "projectedVersion" | "lastProjectionStatus" | "stateVector">;
type Probe = { kind: "loading" | "unsupported" | "error" | "readOnly" } | { kind: "available"; room: Room };

async function tenantRequest<T>(path: string, body: unknown, getToken: AccessTokenGetter, signal?: AbortSignal): Promise<Response> {
  return fetch(`/api/collab/${path}`, {
    method: path === "capability" ? "GET" : "POST",
    headers: {
      Authorization: `Bearer ${await getToken()}`,
      ...(path === "capability" ? {} : { "Content-Type": "application/json" }),
    },
    ...(path === "capability" ? {} : { body: JSON.stringify(body) }),
    signal,
  });
}

export function useCollabCapability(url: string, getToken: AccessTokenGetter, fileName: string, roomTokenUrl?: string): Probe {
  const [probe, setProbe] = useState<Probe>({ kind: "loading" });
  const fileToken = extractFileToken(url);
  const roomFileToken = extractFileToken(roomTokenUrl ?? url);
  const identity = (fileToken && extractFilePathFromToken(fileToken)) || fileName;
  const previousIdentity = useRef(identity);
  useEffect(() => {
    const sameFile = previousIdentity.current === identity;
    previousIdentity.current = identity;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      setProbe({ kind: "error" });
      controller.abort();
    }, 5_000);
    // A refreshed signed link for the same document should not unmount the
    // viewer while its new room credential is being checked.
    if (!sameFile) setProbe({ kind: "loading" });
    if (!fileToken || !roomFileToken) {
      window.clearTimeout(timeout);
      setProbe({ kind: "unsupported" });
      return () => controller.abort();
    }
    void (async () => {
      try {
        const capability = await tenantRequest("capability", null, getToken, controller.signal);
        if (isUnsupportedCapability(capability.status)) {
          setProbe({ kind: "unsupported" });
          return;
        }
        if (!capability.ok) throw new Error("capability failed");
        const minted = await tenantRequest("room", { file_token: roomFileToken }, getToken, controller.signal);
        if (minted.status === 403) {
          setProbe({ kind: "readOnly" });
          return;
        }
        if (isUnsupportedCapability(minted.status)) {
          setProbe({ kind: "unsupported" });
          return;
        }
        if (!minted.ok) throw new Error("room mint failed");
        setProbe({ kind: "available", room: await minted.json() as Room });
      } catch {
        if (!controller.signal.aborted) setProbe({ kind: "error" });
      } finally {
        window.clearTimeout(timeout);
      }
    })();
    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [url, roomTokenUrl, getToken, identity, fileToken, roomFileToken]);
  // A new document must not briefly reuse the previous document's probe
  // result before the effect starts its own capability request.
  return previousIdentity.current === identity ? probe : { kind: "loading" };
}

function CollaborativeEditor({ doc, provider, editable, onEditor, threads, onOpenThread, commentLabel }: {
  doc: Y.Doc; provider: HocuspocusProvider; editable: boolean;
  onEditor: (editor: Editor | null) => void;
  threads: { current: CommentThread[] }; onOpenThread: { current: (id: string) => void };
  commentLabel: { current: string };
}) {
  const extensions = useMemo(() => [
    ...createMarkdownEditorExtensions({ lowlight: createLowlight(common), collaborative: true }),
    Collaboration.configure({ document: doc, field: "default" }),
    CollaborationCaret.configure({ provider, user: { name: "", color: "#6366f1" } }),
    commentAnchorExtension(() => threads.current, (id) => onOpenThread.current(id), () => commentLabel.current),
  ], [doc, provider]);
  const editor = useEditor({
    extensions,
    content: "",
    editable,
    editorProps: {
      attributes: { class: "prose prose-sm dark:prose-invert mx-auto w-full max-w-none min-w-0 focus:outline-none lg:max-w-[720px]" },
    },
  });
  useEffect(() => { editor?.setEditable(editable); }, [editor, editable]);
  useEffect(() => { onEditor(editor); return () => onEditor(null); }, [editor, onEditor]);
  return <EditorContent editor={editor} />;
}

type CollaborativeMarkdownProps = Omit<MarkdownViewerPanelProps, "resize"> & { room: Room; roomTokenUrl?: string } & (
  | { fullscreen: true; resize?: never }
  | { embedded: true; fullscreen?: false; resize?: never; onUnavailable: () => void;
      leading?: ReactNode; contextTitle?: string; onOpenInNewTab?: () => void; hideClose?: boolean }
  | { fullscreen?: false; resize: MarkdownViewerPanelProps["resize"] }
);

export function CollaborativeMarkdownPanel(props: CollaborativeMarkdownProps) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const [editable, setEditable] = useState(false);
  const [synced, setSynced] = useState(false);
  const syncedRef = useRef(false);
  syncedRef.current = synced;
  const embeddedUnavailable = "embedded" in props && props.embedded ? props.onUnavailable : undefined;
  const unavailableRef = useRef(embeddedUnavailable);
  unavailableRef.current = embeddedUnavailable;
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [replyDraft, setReplyDraft] = useState("");
  const [activeThread, setActiveThread] = useState<string | null>(null);
  const [selectionRange, setSelectionRange] = useState<{ from: number; to: number } | null>(null);
  const [selectionPoint, setSelectionPoint] = useState<{ left: number; top: number } | null>(null);
  const [threadPoint, setThreadPoint] = useState<{ left: number; top: number } | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [commentError, setCommentError] = useState(false);
  const [conflictPreview, setConflictPreview] = useState<{
    markdown: string; diskETag: string; yStateVersion: number;
  } | null>(null);
  const [canResolve, setCanResolve] = useState(false);
  const [resolveError, setResolveError] = useState(false);
  const [resolutionPending, setResolutionPending] = useState(false);
  const pendingCreateRef = useRef<{ threadId: string; commentId: string } | null>(null);
  const pendingReplyRef = useRef<string | null>(null);
  const doc = useMemo(() => new Y.Doc(), [props.room.docId]);
  const roomRef = useRef(props.room);
  roomRef.current = props.room;
  const provider = useMemo(() => {
    const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
    return new HocuspocusProvider({
      url: `${scheme}//${window.location.host}/api/collab/ws?tenant=${encodeURIComponent(props.room.tenantId)}`,
      name: props.room.docId,
      document: doc,
      token: async () => {
        if (roomRef.current.expiresAt > Date.now() / 1000 + 60) return roomRef.current.roomToken;
        const fileToken = extractFileToken(props.roomTokenUrl ?? props.url);
        if (!fileToken) throw new Error("File credential unavailable");
        const response = await tenantRequest("room", { file_token: fileToken }, props.getToken);
        if (!response.ok) throw new Error("Room credential refresh failed");
        roomRef.current = await response.json() as Room;
        return roomRef.current.roomToken;
      },
      onSynced: () => { setSynced(true); setError(false); },
      onStatus: ({ status: connectionStatus }: { status: string }) => setConnected(connectionStatus === "connected"),
      onAuthenticationFailed: () => {
        setError(true);
        if (!syncedRef.current) unavailableRef.current?.();
      },
    });
  }, [doc, props.room.docId, props.room.tenantId, props.url, props.roomTokenUrl, props.getToken]);
  useEffect(() => {
    if (!embeddedUnavailable || synced) return;
    const timeout = window.setTimeout(embeddedUnavailable, 7_000);
    return () => window.clearTimeout(timeout);
  }, [embeddedUnavailable, synced]);
  useEffect(() => {
    const onUpdate = () => setRevision((value) => value + 1);
    doc.on("update", onUpdate);
    const tokenTimer = window.setInterval(() => {
      void Promise.resolve(provider.sendToken()).catch(() => setError(true));
    }, 60_000);
    return () => {
      window.clearInterval(tokenTimer);
      doc.off("update", onUpdate);
      provider.destroy();
      doc.destroy();
    };
  }, [doc, provider]);
  useEffect(() => {
    if (!synced) return;
    let cancelled = false;
    const poll = async () => {
      try {
        if (roomRef.current.expiresAt < Date.now() / 1000 + 60) {
          const fileToken = extractFileToken(props.roomTokenUrl ?? props.url);
          if (!fileToken) throw new Error("File credential unavailable");
          const renewed = await tenantRequest("room", { file_token: fileToken }, props.getToken);
          if (!renewed.ok) throw new Error("Room credential refresh failed");
          roomRef.current = await renewed.json() as Room;
        }
        const response = await tenantRequest("status", {
          roomToken: roomRef.current.roomToken, docId: roomRef.current.docId,
        }, props.getToken);
        if (!response.ok) throw new Error("Status unavailable");
        if (!cancelled) {
          setStatus(await response.json() as Status);
          setError(false);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    };
    void poll();
    const timer = window.setInterval(() => { void poll(); }, 1_500);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [synced, props.getToken, props.url, props.roomTokenUrl]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch("/api/me", {
          headers: { Authorization: `Bearer ${await props.getToken()}` },
        });
        if (!response.ok) return;
        const identity = await response.json() as { is_tenant_admin?: boolean; is_platform_admin?: boolean };
        if (!cancelled) setCanResolve(Boolean(identity.is_tenant_admin || identity.is_platform_admin));
      } catch { /* Review remains read-only if identity is unavailable. */ }
    })();
    return () => { cancelled = true; };
  }, [props.getToken]);
  useEffect(() => {
    if (status?.lastProjectionStatus !== "external_conflict") {
      setConflictPreview(null);
      setResolutionPending(false);
      return;
    }
    let cancelled = false;
    void tenantRequest("conflict", {
      docId: roomRef.current.docId, roomToken: roomRef.current.roomToken,
    }, props.getToken).then(async (response) => {
      if (response.ok && !cancelled) setConflictPreview(await response.json() as {
        markdown: string; diskETag: string; yStateVersion: number;
      });
    }).catch(() => { if (!cancelled) setResolveError(true); });
    return () => { cancelled = true; };
  }, [status?.lastProjectionStatus, status?.yStateVersion, status?.sourceETag, props.getToken]);
  const localVector = useMemo(() => Y.encodeStateVector(doc), [doc, revision]);
  const pendingLocal = synced && (!status || !durableVectorCovers(status.stateVector, localVector));
  useEffect(() => {
    if (!pendingLocal) return;
    const protect = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", protect);
    return () => window.removeEventListener("beforeunload", protect);
  }, [pendingLocal]);
  const threads = useMemo(() => Object.values(doc.getMap("comments").toJSON()) as CommentThread[], [doc, revision]);
  const threadsRef = useRef(threads);
  threadsRef.current = threads;
  const commentLabel = useRef(t("viewer.collabComments"));
  commentLabel.current = t("viewer.collabComments");
  const openThreadRef = useRef<(id: string) => void>(() => {});
  const clampLeft = (left: number) => Math.max(8, Math.min(left, (contentRef.current?.clientWidth ?? 320) - 328));
  openThreadRef.current = (id) => {
    setActiveThread(id);
    const thread = threadsRef.current.find((item) => item.id === id);
    const range = editor && thread && resolveCommentRange(editor, thread);
    const box = contentRef.current?.getBoundingClientRect();
    if (editor && range && box) {
      const point = editor.view.coordsAtPos(range.to);
      setThreadPoint({ left: clampLeft(point.left - box.left), top: point.bottom - box.top + contentRef.current!.scrollTop + 6 });
    } else setThreadPoint({ left: 8, top: contentRef.current?.scrollTop ?? 0 });
  };
  useEffect(() => {
    if (!editor) return;
    const update = () => {
      const { from, to } = editor.state.selection;
      if (from === to) { setSelectionRange(null); setSelectionPoint(null); return; }
      const box = contentRef.current?.getBoundingClientRect();
      if (!box) return;
      const point = editor.view.coordsAtPos(to);
      setSelectionRange({ from, to });
      setSelectionPoint({ left: clampLeft(point.left - box.left), top: point.bottom - box.top + contentRef.current!.scrollTop + 6 });
    };
    editor.on("selectionUpdate", update);
    return () => { editor.off("selectionUpdate", update); };
  }, [editor]);
  useEffect(() => {
    if (editor && !editor.isDestroyed) editor.view.dispatch(editor.state.tr.setMeta("collabCommentRefresh", revision));
  }, [editor, revision]);
  const submitComment = async (action: "create" | "reply" | "resolve", threadId: string,
                               commentId: string, anchor?: object | null, resolved?: boolean) => {
    try {
      const mention = roomRef.current.originAgentId && roomRef.current.originSessionId
        ? { kind: "agent", id: roomRef.current.originAgentId,
            targetSessionId: roomRef.current.originSessionId }
        : null;
      const response = await tenantRequest("comment", {
        docId: roomRef.current.docId, roomToken: roomRef.current.roomToken,
        action, threadId, commentId,
        text: action === "reply" ? replyDraft : commentDraft,
        anchor, resolved,
        mention: action === "create" ? mention : null,
      }, props.getToken);
      if (!response.ok) throw new Error("Comment command rejected");
      setCommentDraft(""); setReplyDraft(""); setCommentError(false);
      if (action === "create") { pendingCreateRef.current = null; setActiveThread(null); setSelectionRange(null); }
      if (action === "reply") pendingReplyRef.current = null;
    } catch { setCommentError(true); }
  };
  const createThread = () => {
    if (!editor || !commentDraft.trim()) return;
    const { from, to } = selectionRange ?? editor.state.selection;
    const anchor = createCommentAnchor(editor, from, to);
    pendingCreateRef.current ??= { threadId: crypto.randomUUID(), commentId: crypto.randomUUID() };
    void submitComment("create", pendingCreateRef.current.threadId,
      pendingCreateRef.current.commentId, anchor);
  };
  const resolveConflict = async (mode: "keep_document" | "use_external") => {
    if (!conflictPreview || pendingLocal) return;
    try {
      const response = await tenantRequest("resolve", {
        docId: roomRef.current.docId, roomToken: roomRef.current.roomToken,
        mode, diskETag: conflictPreview.diskETag,
        expectedVersion: conflictPreview.yStateVersion,
      }, props.getToken);
      if (!response.ok) throw new Error("Review changed");
      setResolutionPending(true);
      setResolveError(false);
    } catch { setResolveError(true); }
  };
  let saveState: CollaborativeSaveState = "connecting";
  if (error) saveState = "reconnecting";
  else if (!connected) saveState = synced ? "reconnecting" : "connecting";
  else if (status?.lastProjectionStatus === "external_conflict") saveState = "externalConflict";
  else if (status?.lastProjectionStatus === "projection_failed") saveState = "projectionFailed";
  else if (!status || !durableVectorCovers(status.stateVector, localVector)) saveState = "pendingDocument";
  else if (status.projectedVersion < status.yStateVersion) saveState = "savedDocument";
  else saveState = "publishedFile";
  const contents = (
    <div className="flex h-full flex-col bg-background">
      <div className="flex min-h-[53px] items-center gap-2 border-b px-3">
        {"embedded" in props && props.embedded && props.leading}
        <FileText className="size-4 shrink-0" />
        <MarkdownViewerSaveStatus collaborativeState={saveState} />
        <div className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">{props.fileName}</span>
          {"embedded" in props && props.embedded && props.contextTitle &&
            <span className="block truncate text-xs text-muted-foreground">{props.contextTitle}</span>}
        </div>
        {synced && <Button variant="ghost" size="sm" onClick={() => {
          setSelectionRange(null);
          setSelectionPoint(null);
          setThreadPoint({ left: 8, top: contentRef.current?.scrollTop ?? 0 });
          setActiveThread("new");
        }}>{t("viewer.collabComments")}</Button>}
        <Button variant="outline" size="sm" disabled={!synced || saveState === "externalConflict"}
          onClick={() => setEditable((value) => !value)}>
          {editable ? t("viewer.collabRead") : t("viewer.collabEdit")}
        </Button>
        {"embedded" in props && props.embedded && props.onOpenInNewTab &&
          <Button variant="ghost" size="icon" onClick={props.onOpenInNewTab} aria-label={t("viewer.openInNewWindow")}>
            <ExternalLink className="size-4" />
          </Button>}
        {!("embedded" in props && props.embedded && props.hideClose) &&
        <Button variant="ghost" size="icon" onClick={props.onClose} disabled={pendingLocal}
          title={pendingLocal ? t("viewer.collabPendingClose") : t("viewer.closeViewer")}
          aria-label={pendingLocal ? t("viewer.collabPendingClose") : t("viewer.closeViewer")}>
          <X className="size-4" />
        </Button>}
      </div>
      {saveState === "externalConflict" && <p role="alert" className="m-3 rounded border border-amber-500 p-2 text-sm">{t("viewer.collabExternalConflict")}</p>}
      {saveState === "externalConflict" && conflictPreview && <section className="mx-3 mb-3 space-y-2 rounded border p-2 text-xs">
        <h3 className="font-semibold">{t("viewer.collabReviewConflict")}</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          <div><h4 className="font-medium">{t("viewer.collabDocumentVersion")}</h4>
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2">{editor ? restoreWikiLinks(editor.markdown?.serialize(editor.getJSON()) ?? "") : ""}</pre></div>
          <div><h4 className="font-medium">{t("viewer.collabFileVersion")}</h4>
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2">{conflictPreview.markdown}</pre></div>
        </div>
        {canResolve ? <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={pendingLocal || resolutionPending}
            onClick={() => void resolveConflict("keep_document")}>{t("viewer.collabKeepDocument")}</Button>
          <Button size="sm" variant="outline" disabled={pendingLocal || resolutionPending}
            onClick={() => void resolveConflict("use_external")}>{t("viewer.collabUseFile")}</Button>
        </div> : <p>{t("viewer.collabAskAdmin")}</p>}
        {resolutionPending && <p role="status">{t("viewer.collabResolutionPending")}</p>}
        {resolveError && <p role="alert" className="text-destructive">{t("viewer.collabReviewChanged")}</p>}
      </section>}
      {error && <p role="alert" className="m-3 rounded border border-destructive p-2 text-sm">{t("viewer.collabConnectionError")}</p>}
      <div ref={contentRef} className="relative min-h-0 flex-1 overflow-auto p-4">
        {synced ? <CollaborativeEditor doc={doc} provider={provider} editable={editable} onEditor={setEditor}
          threads={threadsRef} onOpenThread={openThreadRef} commentLabel={commentLabel} />
          : <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner className="size-4" />{t("viewer.collabConnecting")}</div>}
        {synced && selectionRange && selectionPoint && activeThread !== "new" && <Button size="sm" className="absolute z-20 shadow-lg"
          style={{ left: selectionPoint.left, top: selectionPoint.top }}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => { setActiveThread("new"); setThreadPoint(selectionPoint); }}>
          {t("viewer.collabAddComment")}
        </Button>}
        {synced && activeThread === "new" && threadPoint && <section className="absolute z-30 w-72 max-w-[calc(100%-1rem)] rounded border bg-background p-3 shadow-xl"
          style={{ left: threadPoint.left, top: threadPoint.top }} aria-label={t("viewer.collabNewComment")}>
          <div className="flex items-center justify-between"><strong className="text-xs">{t("viewer.collabNewComment")}</strong>
            <Button size="sm" variant="ghost" onClick={() => setActiveThread(null)} aria-label={t("viewer.collabCloseThread")}>×</Button></div>
          <div className="flex gap-2">
          <input className="min-w-0 flex-1 rounded border bg-background px-2 text-sm"
            aria-label={t("viewer.collabNewComment")} placeholder={t("viewer.collabNewComment")}
            value={commentDraft} onChange={(event) => setCommentDraft(event.target.value)} />
          <Button size="sm" disabled={!commentDraft.trim()} onClick={createThread}>{t("viewer.collabComment")}</Button>
          </div>
        {roomRef.current.originAgentId && roomRef.current.originSessionId &&
          <p className="mt-1 text-xs text-muted-foreground">{t("viewer.collabAgentMention")}</p>}
        {commentError && <p role="alert" className="text-xs text-destructive">{t("viewer.collabCommentError")}</p>}
        </section>}
        {synced && activeThread && activeThread !== "new" && threadPoint && threads.filter((thread) => thread.id === activeThread).map((thread) =>
          <section key={thread.id} className="absolute z-30 w-80 max-w-[calc(100%-1rem)] rounded border bg-background p-3 text-xs shadow-xl"
            style={{ left: threadPoint.left, top: threadPoint.top }} aria-label={t("viewer.collabComments")}>
            <div className="flex justify-between gap-2">
              <span className="font-medium">{thread.detached ? t("viewer.collabDetached")
                : thread.anchor?.quote ? `“${thread.anchor.quote}”` : t("viewer.collabDocumentComment")}</span>
              <Button size="sm" variant="ghost" onClick={() => setActiveThread(null)} aria-label={t("viewer.collabCloseThread")}>×</Button>
              <Button size="sm" variant="ghost" onClick={() => void submitComment("resolve", thread.id, thread.id,
                null, !thread.resolved)}>{thread.resolved ? t("viewer.collabReopen") : t("viewer.collabResolve")}</Button>
            </div>
            {thread.comments?.map((comment) => <p key={comment.id} className="mt-1 whitespace-pre-wrap break-words">
              <strong>{comment.author?.displayNameSnapshot ?? t("viewer.collabUnknownAuthor")}: </strong>{comment.text}
            </p>)}
            {commentError && <p role="alert" className="mt-1 text-destructive">{t("viewer.collabCommentError")}</p>}
            {activeThread === thread.id ? <div className="mt-2 flex gap-2">
              <input className="min-w-0 flex-1 rounded border bg-background px-2" value={replyDraft}
                aria-label={t("viewer.collabReply")} onChange={(event) => setReplyDraft(event.target.value)} />
              <Button size="sm" disabled={!replyDraft.trim()} onClick={() => {
                pendingReplyRef.current ??= crypto.randomUUID();
                void submitComment("reply", thread.id, pendingReplyRef.current);
              }}>{t("viewer.collabReply")}</Button>
            </div> : <Button size="sm" variant="ghost" onClick={() => setActiveThread(thread.id)}>{t("viewer.collabReply")}</Button>}
          </section>)}
      </div>
    </div>
  );
  if (props.fullscreen) return <div className="h-screen bg-background text-foreground">{contents}</div>;
  if ("embedded" in props && props.embedded) return <div className="h-full min-h-0 bg-background text-foreground">{contents}</div>;
  if (mobile) return <Dialog open onOpenChange={(open) => { if (!open && !pendingLocal) props.onClose(); }}>
    <DialogContent hideCloseButton className="h-full max-h-full w-full max-w-full rounded-none p-0">
      <DialogTitle className="sr-only">{props.fileName}</DialogTitle>{contents}
    </DialogContent>
  </Dialog>;
  const resize = props.resize as MarkdownViewerPanelProps["resize"];
  return <div className="relative h-full shrink-0 border-l" style={{ width: resize.width }}>
    <div role="separator" aria-orientation="vertical" aria-label={t("viewer.dragToResize")}
      onPointerDown={resize.onPointerDown} onPointerMove={resize.onPointerMove}
      onPointerUp={resize.onPointerUp} onDoubleClick={resize.onDoubleClick}
      className="absolute -left-[3px] top-0 z-20 h-full w-1.5 cursor-col-resize touch-none" />
    {contents}
  </div>;
}
